import { SerializedError } from '@reduxjs/toolkit';
import { FocusEvent, useCallback, useEffect, useState } from 'react';
import { StringSchema, ValidationError } from 'yup';

type UseTextFieldValidationParams = {
  validator: StringSchema;
  onValidationSuccess: (value: string) => void;
  error: SerializedError | null;
  onValidationError: (error: SerializedError | null) => void;
};

const useTextFieldValidation = ({
  validator,
  onValidationSuccess,
  error,
  onValidationError,
}: UseTextFieldValidationParams) => {
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    onValidationError(null);
  }, [isDirty, onValidationError]);

  const validate = useCallback(
    (value: string) => {
      if (!isDirty && error) {
        return false;
      }
      try {
        validator.validateSync(value);
      } catch (err) {
        const { message } = err as ValidationError;
        onValidationError({ message });
        return false;
      }
      return true;
    },
    [isDirty, error, validator, onValidationError]
  );

  const handleBlur = useCallback(
    (value: string) => {
      const isValid = validate(value);
      if (!isValid) {
        return;
      }
      onValidationSuccess(value);
    },
    [validate, onValidationSuccess]
  );

  const onChange = useCallback(() => {
    setIsDirty(true);
  }, []);

  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsDirty(false);
      const { value } = event.target;
      handleBlur(value);
    },
    [handleBlur]
  );

  return {
    onChange,
    onBlur,
  };
};

export default useTextFieldValidation;
